import { Card, CardActionArea, CardContent, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import { schemeSx } from "@/src/theme/schemeStyles";

import {
  VEHICLE_TYPE_ICONS,
  VEHICLE_TYPE_TEST_IDS
} from "@/src/components/molecules/VehicleSelectCard/vehicleSelectCard.constants";
import { buildWizardCardSx } from "@/src/components/molecules/VehicleSelectCard/vehicleSelectCardWizardView.styles";

import { VehicleType } from "@/src/types/types";

interface VehicleSelectCardWizardViewProps {
  selectedVehicleType: VehicleType;
  onVehicleTypeChange: (value: VehicleType) => void;
  vehicleLabel: string;
  vehicleType: VehicleType;
}

const cardActionAreaSx = {
  height: "100%",
  minWidth: 0,
  display: "flex",
  alignItems: "stretch",
  ".MuiCardActionArea-focusHighlight": {
    backgroundColor: "transparent"
  }
};

const cardContentSx = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  boxSizing: "border-box",
  px: { xs: 0.75, sm: 1 },
  py: { xs: 0.25, sm: 0.5 },
  gap: { xs: "0", sm: "0" },
  minWidth: { xs: "100%", sm: "180px", md: "180px" },
  height: { xs: "55px", sm: "76px" }
};

const buildIconSx = (isSelected: boolean): SxProps<Theme> =>
  schemeSx((muiTheme) => ({
    width: { xs: 28, sm: 30 },
    height: { xs: 28, sm: 30 },
    fontSize: { xs: 28, sm: 30 },
    color: isSelected
      ? muiTheme.palette.text.primary
      : muiTheme.palette.text.secondary
  }));

const buildLabelSx = (isSelected: boolean): SxProps<Theme> =>
  schemeSx((muiTheme) => ({
    color: isSelected
      ? muiTheme.palette.text.primary
      : muiTheme.palette.text.secondary,
    lineHeight: 1.25,
    fontSize: { xs: "0.875rem", sm: "1.125rem" }
  }));

const VehicleSelectCardWizardView = (
  props: VehicleSelectCardWizardViewProps
) => {
  const {
    vehicleType,
    selectedVehicleType,
    onVehicleTypeChange,
    vehicleLabel
  } = props;
  const isSelected = selectedVehicleType === vehicleType;
  const VehicleIcon = VEHICLE_TYPE_ICONS[vehicleType];

  return (
    <Card
      raised={isSelected}
      sx={buildWizardCardSx(isSelected)}
      elevation={0}
      data-testid={VEHICLE_TYPE_TEST_IDS[vehicleType]}
    >
      <CardActionArea
        onClick={() => onVehicleTypeChange(vehicleType)}
        sx={cardActionAreaSx}
      >
        <CardContent sx={cardContentSx}>
          <VehicleIcon sx={buildIconSx(isSelected)} />
          <Typography variant="subtitle1" sx={buildLabelSx(isSelected)}>
            {vehicleLabel}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default VehicleSelectCardWizardView;
